import { type CommonAction, response } from "@iflyrpa/share";
import { z } from "zod";
import { ActionCommonParamsSchema } from "../../types";
import type { ParsedVerifyDecision } from "../../utils/douyin/verify-parser";
import { USER_MESSAGE } from "../../utils/errorMessages";
import { Http } from "../../utils/http";

const { generateCsrfTokenAdvanced } = require("../../utils/douyin/douyinSafe/csrfToken.js");
const { getABogus } = require("../../utils/douyin/douyinSafe/a_bogus.js");
const { generateDtrait } = require("../../utils/douyin/douyinSafe/dtrait.js");

export const DouyinGetVerifyQrCodeParamsSchema = ActionCommonParamsSchema.extend({
    decision: z.record(z.string(), z.unknown()),
    extraParam: z
        .object({
            browserInfo: z.object({
                screen_width: z.string().optional(),
                screen_height: z.string().optional(),
                browser_language: z.string().optional(),
                browser_platform: z.string().optional(),
                browser_name: z.string().optional(),
                "sec-ch-ua-platform": z.string().optional(),
                "sec-ch-ua": z.string().optional(),
                userAgent: z.string().optional(),
            }),
        })
        .optional(),
});

export type DouyinGetVerifyQrCodeParams = Omit<z.infer<typeof DouyinGetVerifyQrCodeParamsSchema>, "decision"> & {
    decision: ParsedVerifyDecision;
};

export interface QrCodeResult {
    qrcode: string; // base64 编码的二维码图片
    token: string; // 二维码 token，用于后续轮询扫码状态
    expire_time: number; // 过期时间戳（秒）
    qrcode_index_url: string; // 二维码跳转地址
    app_name: string; // 应用名称
    web_name: string; // 网页名称
    captcha: string; // 人机验证
    error_code: number; // 错误码
    is_frontier: boolean; // 是否为前台验证
}

export type DouyinGetVerifyQrCodeAction = CommonAction<DouyinGetVerifyQrCodeParams, QrCodeResult>;

export const douyinGetVerifyQrCode: DouyinGetVerifyQrCodeAction = async (task, params) => {
    const userAgent =
        params.userAgent ||
        "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/134.0.0.0 Safari/537.38";

    // 直接使用传入的 decision 对象
    const decision = params.decision;
    if (!decision) {
        return response(414, "无效的 decision，解析失败", "" as any);
    }

    if (!decision.verify_ways?.includes("qr_code_verify")) {
        return response(
            414,
            `当前验证方式不支持二维码，支持的方式: ${decision.verify_ways?.join(", ") || "无"}`,
            "" as any,
        );
    }

    try {
        task.logger.info("开始获取抖音扫码验证二维码...");

        // biz_trace_id 优先从 cookie 中获取，取不到则降级用 log_id 末8位
        const bizTraceIdFromCookie = params.cookies.find((e: any) => e.name === "biz_trace_id")?.value || "";
        const logId = decision.event_params?.log_id || "";
        const bizTraceId = bizTraceIdFromCookie || logId.slice(-8).toLowerCase() || "00000000";

        const msToken = params.cookies.find((e: any) => e.name === "msToken")?.value || "";
        const passportCsrfToken = params.cookies.find((e: any) => e.name === "passport_csrf_token")?.value || "";

        const headers = {
            cookie: params.cookies.map((it: any) => `${it.name}=${it.value}`).join(";"),
            origin: "https://creator.douyin.com",
            referer:
                "https://creator.douyin.com/creator-micro/content/post/image?default-tab=3&enter_from=publish_page&media_type=image&type=new",
            Accept: "application/json, text/plain, */*",
            "Accept-Encoding": "gzip, deflate, br, zstd",
            "sec-ch-ua-platform": params.extraParam?.browserInfo?.["sec-ch-ua-platform"] || "",
            "sec-ch-ua": params.extraParam?.browserInfo?.["sec-ch-ua"] || "",
            "sec-ch-ua-mobile": "?0",
            "user-agent": userAgent,
            "sec-fetch-site": "same-origin",
            "sec-fetch-mode": "cors",
            "sec-fetch-dest": "empty",
            "accept-language": "zh-CN,zh;q=0.9",
            priority: "u=1, i",
        };

        // 构建 POST body
        const qrCodeBody: Record<string, string> = {
            encrypt_uid: decision.encrypt_uid || "",
            verify_ticket: "",
            copywriting_key: decision.common_params?.copywriting_key || "creator",
            new_verify_flow: decision.common_params?.new_verify_flow || "",
            aid: "2906",
            verify_type: "",
            next: "https://www.douyin.com/",
            need_record: "1",
            scene: "secondary_verify",
            verify_scene: decision.event_params?.verify_scene || "creator",
            verify_reason: decision.event_params?.verify_reason || "gateway_web_authlv_check",
            std_verify_way: "qr_code_verify",
            source: "app",
            new_authn_sdk_version: "1.0.0.404-web",
        };
        const qrCodeBodyStr = new URLSearchParams(qrCodeBody).toString();

        // 构建 URL 查询参数（先不含 a_bogus）
        const qrCodeQueryParams: Record<string, string> = {
            passport_jssdk_version: "5.1.2",
            passport_jssdk_type: "lite",
            is_from_ttaccountsdk: "1",
            aid: "2906",
            language: "zh",
            account_app_language: "zh-CN",
            new_authn_sdk_version: "1.0.0.404-web",
            is_vcd: "1",
            biz_trace_id: bizTraceId,
            msToken,
            a_bogus: "",
        };

        // 生成 a_bogus
        const qrCodeQueryStr = new URLSearchParams(qrCodeQueryParams).toString();
        const qrCodeUrl = `https://creator.douyin.com/passport/web/get_qrcode/?${qrCodeQueryStr}`;
        const aBogusQr = getABogus(qrCodeUrl, qrCodeBodyStr, userAgent);
        qrCodeQueryParams.a_bogus = aBogusQr;
        const finalQueryStr = new URLSearchParams(qrCodeQueryParams).toString();

        // 生成 CSRF Token 与 sessionDtrait
        const csrfTokenQr = generateCsrfTokenAdvanced({
            cookies: params.cookies,
            url: "https://creator.douyin.com/passport/web/get_qrcode/",
            method: "POST",
            userAgent,
        });
        const sessionDtraitQr = generateDtrait("/passport/web/get_qrcode/");

        const qrHttp = new Http({
            headers: {
                ...headers,
                "Content-Type": "application/x-www-form-urlencoded",
                "x-secsdk-csrf-token": csrfTokenQr,
                "x-tt-passport-csrf-token": passportCsrfToken,
                "x-tt-passport-trace-id": bizTraceId,
                "x-tt-passport-verify-portrait": "",
                "x-tt-session-dtrait": sessionDtraitQr || "",
            },
        });

        // 拦截原始响应，记录真实返回内容
        qrHttp.addResponseInterceptor((res) => {
            task.logger.info(`[douyinGetVerifyQrCode] 原始响应 status=${res.status} data=${JSON.stringify(res.data)}`);
            console.log("[douyinGetVerifyQrCode] 原始响应:", JSON.stringify(res.data));
            return undefined;
        });

        const qrCodeResponse = await qrHttp.api<{
            data: {
                qrcode: string;
                token: string;
                expire_time: number;
                error_code: number;
                qrcode_index_url: string;
                app_name: string;
                web_name: string;
                captcha: string;
                is_frontier: boolean;
                [key: string]: any;
            };
            message: string;
        }>(
            {
                method: "post",
                url: `https://creator.douyin.com/passport/web/get_qrcode/?${finalQueryStr}`,
                data: qrCodeBodyStr,
            },
            { timeout: 10 * 1000 },
        );

        const qrData = qrCodeResponse?.data;
        task.logger.info(`获取二维码响应: error_code=${qrData?.error_code}, message=${qrCodeResponse?.message}`);

        if (qrData?.error_code !== 0) {
            // error_code 已由上面的 info 日志记录，用户侧不暴露原始错误码
            return response(414, USER_MESSAGE.QRCODE_FETCH_FAILED, "" as any);
        }

        return response(0, "获取二维码成功", {
            qrcode: qrData.qrcode,
            token: qrData.token,
            expire_time: qrData.expire_time,
            qrcode_index_url: qrData.qrcode_index_url,
            app_name: qrData.app_name,
            web_name: qrData.web_name,
            captcha: qrData.captcha,
            error_code: qrData.error_code,
            is_frontier: qrData.is_frontier,
        });
    } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        task.logger.warn(`[douyinGetVerifyQrCode] 获取二维码失败: ${msg}`);
        return response(500, USER_MESSAGE.QRCODE_FETCH_FAILED, "" as any);
    }
};
